import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { adminApi } from "../lib/admin-api";
import type { AdminAccess } from "../lib/permissions";
import { supabase } from "../lib/supabase";
import { AuthContext, type AdminIdentity } from "./AuthContext";

type MeResponse = {
  access: AdminAccess;
  user: AdminIdentity;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState<AdminAccess | null>(null);
  const [identity, setIdentity] = useState<AdminIdentity | null>(null);

  const clear = useCallback(() => {
    setAccess(null);
    setIdentity(null);
  }, []);

  const refreshAccess = useCallback(async () => {
    const current = await adminApi<MeResponse>("me");
    setAccess(current.access);
    setIdentity(current.user);
  }, []);

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      if (data.session) {
        try {
          await refreshAccess();
        } catch {
          await supabase.auth.signOut({ scope: "local" });
          if (active) clear();
        }
      }
      if (active) setLoading(false);
    };
    void initialize();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      queueMicrotask(() => {
        if (!active) return;
        if (!session) {
          clear();
          setLoading(false);
        }
      });
    });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, [clear, refreshAccess]);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      await refreshAccess();
    } catch (error) {
      await supabase.auth.signOut({ scope: "local" });
      clear();
      throw error;
    } finally {
      setLoading(false);
    }
  }, [clear, refreshAccess]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    clear();
  }, [clear]);

  const value = useMemo(() => ({ loading, access, identity, login, logout, refreshAccess }), [access, identity, loading, login, logout, refreshAccess]);
  return <AuthContext value={value}>{children}</AuthContext>;
}
